import React, {Component} from "react"

import {isGlobalAdmin} from "@irati/identity/helpers/user/role"
import {Team, User, TeamState} from "@irati/types"
import {H3} from "@irati/ui/src//Typography/Heading"
import Button from "@irati/ui/src/Button"
import Loading from "@irati/ui/src/Loading"
import {Label2, Para} from "@irati/ui/src/Labels"
import {FormattedMessage} from "@irati/utils/lang"
import Pill from "@irati/ui/src/Pill"
import Flex from "@irati/ui/src/Flex"
import authentication from "@irati/auth"
import teamService from "@irati/identity/services/teams"
import userService from "@irati/identity/services/users"

import {Table} from "../styled"
import InvitePeople from "./InvitePeople"

interface Props {
  setIsStepCompletable: (value: boolean) => void,
  setOptionsVisibility: (value: boolean) => void
}

interface State {
  teams: Team[],
  users: User[],
  total: number,
  cursor?: string,
  hasMore: boolean,
  usersCount: number,
  isInviting?: boolean,
  loadingUsers?: Promise<void>
}

class PeopleList extends Component<Props, State> {
  state: State = {
    users: [],
    teams: [],
    cursor: undefined,
    hasMore: true,
    usersCount: 0
  }

  componentDidMount() {
    const loadingUsers = teamService
      .list(authentication.userTenant().uuid, {state: TeamState.Generic}, {fetchPolicy: "network-only"})
      .then((teams) => {
        this.setState({teams})
        return this.getUserList()
      })
    this.setState({loadingUsers})
  }

  isLoadingUsers = false

  getMoreUsers = () => {
    const {cursor, users} = this.state
    if (!this.isLoadingUsers && cursor) {
      this.getUserList(users, cursor)
    }
  }

  getUserList = (users: User[] = [], cursor?: string) => {
    this.isLoadingUsers = true
    const {setOptionsVisibility} = this.props
    const params = {
      businessId: authentication.userTenant().uuid,
      enabled: true,
      cursor,
      sort: "lastname"
    }
    return userService.getUsersForBusiness(params, {fetchPolicy: "network-only"}).then(({results, total, cursor: newCursor}) => {
      const newUsers = results
      const allUsers = users.concat(newUsers)
      allUsers.forEach((user) => {
        user.resolvedTeams = user.teamIds.map((teamId) => {
          return this.state.teams.find((t) => t.uuid === teamId)?.public_name
        }).filter((t) => !!t).join(", ")
      })
      const allNonAdminUsers = allUsers.filter((user) => !isGlobalAdmin(authentication.business(), user))

      if (allNonAdminUsers.length === 0) {
        setOptionsVisibility(true)
      } else {
        // else we invited some people, meaning options should stay hidden and step completable
        this.props.setIsStepCompletable(true)
      }
      this.setState({
        users: allUsers,
        usersCount: total,
        cursor: newCursor
      })
      this.isLoadingUsers = false
    })
  }

  startInviting = () => {
    this.props.setOptionsVisibility(false)
    this.setState({isInviting: true})
  }

  onCallback = () => {
    this.getUserList()
    this.setState({isInviting: false})
  }

  render() {
    const {isInviting, loadingUsers, users, teams, usersCount} = this.state
    // TODO amend this when we'll add the funtionality "Invite another Admin", logic TBD
    const firstTimeInvitingPeople = users.length === 1
    if (isInviting) {
      return <InvitePeople onCallback={this.onCallback} teams={teams} />
    }
    return (
      <Loading load={loadingUsers}>
        <Flex justifyContent="space-between">
          <H3>
            <FormattedMessage id="setup_tenant.steps.invite.peopleList.title" />
            <Pill text={<FormattedMessage id="setup_tenant.steps.invite.peopleList.pill" values={{count: usersCount}} />} margin="0 15px" />
          </H3>
          <Button primary onClick={this.startInviting}>
            {firstTimeInvitingPeople ? (
              <FormattedMessage id="setup_tenant.steps.invite.peopleList.ctaInvite" />
            ) : (
              <FormattedMessage id="setup_tenant.steps.invite.peopleList.ctaInviteMore" />
            )}
          </Button>
        </Flex>
        {!firstTimeInvitingPeople && (
          <Para>
            <FormattedMessage id="setup_tenant.steps.invite.peopleList.description" />
          </Para>
        )}
        <Table>
          <thead>
            <tr>
              <th>
                <Label2 uppercase>
                  <FormattedMessage id="platform.fields.firstName.label" />
                </Label2>
              </th>
              <th>
                <Label2 uppercase>
                  <FormattedMessage id="platform.fields.lastName.label" />
                </Label2>
              </th>
              <th>
                <Label2 uppercase>
                  <FormattedMessage id="platform.fields.email.label" />
                </Label2>
              </th>
              <th>
                <Label2 uppercase>
                  <FormattedMessage id="platform.fields.teams.label" />
                </Label2>
              </th>
              <th>
                <Label2 uppercase>
                  <FormattedMessage id="platform.fields.accessLevel.label" />
                </Label2>
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: User) => (
              <tr key={user.uuid}>
                <td>{user.firstName}</td>
                <td>{user.lastName}</td>
                <td>{user.email}</td>
                <td>{user.resolvedTeams}</td>
                <td>{user.role}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {users.length < usersCount && (
          <Flex justifyContent="space-around">
            <Button onClick={this.getMoreUsers}>
              <FormattedMessage id="setup_tenant.steps.invite.peopleList.loadMore" />
            </Button>
          </Flex>
        )}
      </Loading>
    )
  }
}

export default PeopleList
